import type {
  Component as ReploComponent,
  ReploComponentType,
} from "schemas/component";
import type {
  ProductRef,
  ProductRefOrDynamic,
  StoreProductSummary,
} from "schemas/product";

import * as React from "react";

import BundleDiscountsCustomPropModifier from "@editor/components/editor/customProp/BundleDiscountsCustomPropModifier";
import BundleProductsCustomPropModifier from "@editor/components/editor/customProp/BundleProductsCustomPropModifier";
import { DynamicDataSelector } from "@editor/components/editor/page/DynamicDataSelector";
import { ProductSelectionCombobox } from "@editor/components/editor/page/ProductSelectionCombobox";
import useDynamicDataQuickConnect from "@editor/hooks/useDynamicDataQuickConnect";
import { selectProductSummaries } from "@editor/reducers/commerce-reducer";
import { selectTemplateEditorProduct } from "@editor/reducers/template-reducer";
import { selectOpenPopoverId } from "@editor/reducers/ui-reducer";
import { useEditorSelector } from "@editor/store";

import Label from "@replo/design-system/components/label/Label";
import Popover from "@replo/design-system/components/popover/Popover";
import twMerge from "@replo/design-system/utils/twMerge";
import { Database, Settings2 } from "lucide-react";
import { DynamicDataTargetType } from "replo-runtime/shared/dynamicData";
import { isContextRef } from "replo-runtime/store/ReploProduct";

const ConnectedProductsIndicatorTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    products: ProductRefOrDynamic[];
    isAncestor?: boolean;
    allowMultiple?: boolean;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ products, isAncestor, allowMultiple, onClick }, ref) => {
  const productSummariesFromStore = useEditorSelector(selectProductSummaries);
  const templateProduct = useEditorSelector(selectTemplateEditorProduct);
  if (allowMultiple && products.length < 1) {
    return (
      <button
        ref={ref}
        onClick={onClick}
        className={twMerge(
          "border-border flex h-[32px] items-center rounded-md border-[2px] border-dashed bg-white p-1.5",
        )}
      >
        <Database size="20" className="mr-1" />
        <Label size="sm" label="Select Products" />
      </button>
    );
  }
  const productTilesToRender = products.map((productRef) => {
    if (!productRef) {
      return null;
    }
    if (isContextRef(productRef)) {
      return (
        templateProduct &&
        productSummariesFromStore[Number(templateProduct.productId)]
      );
    }
    return productSummariesFromStore[Number(productRef.productId)];
  });
  const isSingleProductOrEmpty = productTilesToRender.length <= 1;
  if (isSingleProductOrEmpty && !allowMultiple) {
    const productSummary = productTilesToRender[0];
    return (
      <button ref={ref} onClick={onClick} className="flex">
        <div
          className={twMerge(
            "border-dynamic-data flex h-[32px] w-[32px] items-center justify-center overflow-hidden bg-white rounded-md border-[2px]",
            isAncestor && "border-parent-product",
            !productSummary?.featuredImage && "bg-white border-primary-muted",
          )}
        >
          {productSummary?.featuredImage ? (
            <img src={productSummary.featuredImage} />
          ) : (
            <Database size="20" />
          )}
        </div>
      </button>
    );
  }
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={twMerge(
        "border-dynamic-data flex h-[32px] items-center rounded-sm bg-white border-[2px] p-1.5",
        isAncestor && "border-parent-product",
      )}
    >
      <div className="mr-1 flex">
        {productTilesToRender
          .slice(0, 4)
          .map(
            (
              productSummary: StoreProductSummary | null | undefined,
              index: number,
            ) => (
              <div
                key={productSummary?.id ?? index}
                className={twMerge(
                  "border-solid-white flex h-[15px] w-[15px] items-center justify-center overflow-hidden rounded-sm",
                  !productSummary?.featuredImage &&
                    "bg-white border-primary-muted",
                  index !== 0 && "ml-[-5px]",
                )}
              >
                {productSummary?.featuredImage ? (
                  <img src={productSummary.featuredImage} />
                ) : (
                  <Database size="20" />
                )}
              </div>
            ),
          )}
      </div>
      <Label size="sm" label={`${products.length} Products`} />
    </button>
  );
});

ConnectedProductsIndicatorTrigger.displayName =
  "ConnectedProductsIndicatorTrigger";

const ProductSelectionControlBox = ({
  component,
  isAncestor,
}: {
  component: ReploComponent;
  isAncestor?: boolean;
}) => {
  const { dynamicDataProp, handleChangePropValue } =
    useDynamicDataQuickConnect(component);
  if (
    component.type !== "productCollection" &&
    (!component?.props._product ||
      (isContextRef(component.props._product) &&
        component.props._product.ref === "attributes._product"))
  ) {
    return null;
  }

  const isSingleProductProp = component.type === "product";
  if (isSingleProductProp) {
    if (dynamicDataProp && isContextRef(dynamicDataProp.propValue)) {
      return (
        <DynamicDataSelector
          targetType={DynamicDataTargetType.PRODUCT}
          initialPath={dynamicDataProp.propValue.ref.split(".")}
          side="top"
          trigger={
            <ConnectedProductsIndicatorTrigger
              products={[dynamicDataProp.propValue]}
            />
          }
          onChange={handleChangePropValue}
        />
      );
    }
    const existingProductRef = dynamicDataProp?.propValue as ProductRef | null;
    return (
      <ProductSelectionCombobox
        value={existingProductRef}
        onChange={(value: ProductRef | null) =>
          value && handleChangePropValue?.(value)
        }
        allowAutoOpen
        side="top"
        trigger={
          <ConnectedProductsIndicatorTrigger
            products={existingProductRef ? [existingProductRef] : []}
            isAncestor={isAncestor}
          />
        }
      />
    );
  }
  return (
    <ProductSelectionCombobox
      isMultiselect
      value={(dynamicDataProp?.propValue as ProductRef[]) ?? []}
      onChange={(value: ProductRef[]) =>
        value && handleChangePropValue?.(value as ProductRef[])
      }
      side="top"
      trigger={
        <ConnectedProductsIndicatorTrigger
          products={(dynamicDataProp?.propValue as ProductRefOrDynamic[]) ?? []}
          isAncestor={isAncestor}
        />
      }
    />
  );
};

const BundleTargetSelectionControlBox = ({
  component,
  isAncestor,
}: {
  component: ReploComponent;
  isAncestor?: boolean;
}) => {
  const openPopoverId = useEditorSelector(selectOpenPopoverId);
  const isDefaultOpen = openPopoverId === "bundle-product-selector";
  const productsWithVariantsArray = component.props.productsWithVariants ?? [];

  const productRefs = productsWithVariantsArray.map(({ productId }) => ({
    productId,
  }));
  return (
    <div className="flex gap-1.5">
      <Popover.Root
        shouldIgnoreOutsideInteractions={false}
        isDefaultOpen={isDefaultOpen}
      >
        <Popover.Trigger asChild>
          <ConnectedProductsIndicatorTrigger
            allowMultiple
            products={productRefs}
            isAncestor={isAncestor}
          />
        </Popover.Trigger>
        <Popover.Content
          stayInPosition
          shouldPreventDefaultOnInteractOutside={false}
          side="bottom"
          align="center"
          hideCloseButton
        >
          <BundleProductsCustomPropModifier component={component} />
        </Popover.Content>
      </Popover.Root>
      <Popover.Root shouldIgnoreOutsideInteractions={false}>
        <Popover.Trigger asChild>
          <button className="border-border flex h-[32px] w-[32px] bg-white items-center justify-center overflow-hidden rounded-md border-[2px]">
            <Settings2 size={16} />
          </button>
        </Popover.Trigger>
        <Popover.Content
          stayInPosition
          shouldPreventDefaultOnInteractOutside={false}
          side="bottom"
          align="center"
          hideCloseButton
          className="w-[300px]"
        >
          <BundleDiscountsCustomPropModifier component={component} />
        </Popover.Content>
      </Popover.Root>
    </div>
  );
};

const PRODUCT_SELECTOR_COMPONENTS = new Set<ReploComponentType>([
  "product",
  "productCollection",
]);

export const BoundingBoxPropControls = ({
  component,
  isAncestor,
}: {
  component: ReploComponent;
  isAncestor?: boolean;
}) => {
  if (PRODUCT_SELECTOR_COMPONENTS.has(component.type)) {
    return (
      <div className="pointer-events-auto">
        <ProductSelectionControlBox
          component={component}
          isAncestor={isAncestor}
        />
      </div>
    );
  }
  if (component.type === "bundleBuilder") {
    return (
      <div className="pointer-events-auto">
        <BundleTargetSelectionControlBox
          component={component}
          isAncestor={isAncestor}
        />
      </div>
    );
  }
};
